/**
 * Create an archival container.
 *
 * Five steps, ending in access — because "how do I let someone see this?" is
 * the question that follows immediately from "I made an archive", and answering
 * it inside the same flow is the whole point of putting it last.
 */

import {
  ActionIcon,
  Alert,
  Autocomplete,
  Button,
  Card,
  Group,
  Paper,
  Select,
  Stack,
  Stepper,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  createArchiveContainer,
  listArchiveClusters,
  type ArchiveContainer,
  type ArchiveField,
} from "@/shared/api/archive";
import { ArchiveAccessTab } from "./ArchiveAccessTab";
import { CLUSTER_LABEL, clusterTitle } from "./format";

const CLASSIFICATIONS = ["public", "confidential", "pii", "phi"];

const DEFAULT_FIELDS: ArchiveField[] = [
  {
    key: "record_type",
    label: "Record type",
    type: "text",
    facetable: true,
    required: true,
    classification: "public",
  },
  {
    key: "site",
    label: "Site",
    type: "text",
    facetable: true,
    required: false,
    classification: "public",
  },
  {
    key: "product",
    label: "Product",
    type: "text",
    facetable: true,
    required: false,
    classification: "public",
  },
  {
    key: "effective_date",
    label: "Effective date",
    type: "date",
    facetable: true,
    required: false,
    classification: "public",
  },
  {
    key: "retention_class",
    label: "Retention class",
    type: "text",
    facetable: true,
    required: false,
    classification: "public",
  },
];

export function NewArchiveContainerPage() {
  const getToken = useAccessToken();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<ArchiveContainer | null>(null);

  // What this tenant already uses, so a taxonomy grows by being used rather
  // than by someone populating a reference list first.
  const [clusterOptions, setClusterOptions] = useState<string[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        setClusterOptions(await listArchiveClusters(await getToken()));
      } catch {
        setClusterOptions([]);
      }
    })();
  }, [getToken]);

  const [identity, setIdentity] = useState({
    code: "",
    name: "",
    cluster: "C",
    kind: "documents",
    source_system: "",
    description: "",
  });
  const [fields, setFields] = useState<ArchiveField[]>(DEFAULT_FIELDS);
  const [retention, setRetention] = useState({
    retention_class: "15y",
    object_lock_mode: "GOVERNANCE",
  });
  const [storage, setStorage] = useState({
    storage_prefix: "cluster-c/",
    storage_class: "STANDARD",
    content_indexing_enabled: false,
  });

  async function create() {
    setBusy(true);
    try {
      const token = await getToken();
      const container = await createArchiveContainer(token, {
        ...identity,
        ...retention,
        ...storage,
        decommissioned_on: null,
        fields,
      });
      setCreated(container);
      notifications.show({
        title: "Archive created",
        message: "Its metadata fields are live search facets. Grant access to finish.",
        color: "teal",
      });
      setStep(4);
    } catch (err) {
      notifications.show({
        title: "Could not create the archive",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  const canContinue =
    step === 0
      ? identity.code.trim() && identity.name.trim() && identity.source_system.trim()
      : step === 3
        ? storage.storage_prefix.trim()
        : true;

  return (
    <Stack gap="lg">
      <div>
        <Title order={3}>New archival container</Title>
        <Text size="sm" c="dimmed" mt={4}>
          Define what is archived, how it is described, how long it is kept, where it lives, and who
          can see it.
        </Text>
      </div>

      <Paper withBorder p="xl" radius="md" maw={940}>
        <Stepper active={step} onStepClick={created ? undefined : setStep} size="sm">
          <Stepper.Step label="Identity" description="What is archived">
            <Stack gap="sm" mt="xl">
              <Group grow>
                <TextInput
                  label="Code"
                  description="Short handle, unique per tenant"
                  placeholder="QRA"
                  value={identity.code}
                  onChange={(e) =>
                    setIdentity((v) => ({ ...v, code: e.currentTarget.value.toUpperCase() }))
                  }
                />
                {/* Free text with the tenant's existing classifications offered
                    as suggestions. The taxonomy is the customer's: this
                    deployment is configured with BioNTech's capability
                    clusters, and another customer types "Clinical" instead.
                    Autocomplete rather than Select so a new value needs no
                    reference screen to be created first. */}
                <Autocomplete
                  label="Classification"
                  description="Yours to define — pick one in use, or type a new one"
                  placeholder="e.g. C, or Clinical Trial Records"
                  value={identity.cluster}
                  data={clusterOptions}
                  maxLength={64}
                  onChange={(value) => {
                    setIdentity((v) => ({ ...v, cluster: value }));
                    const slug =
                      value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/^-+|-+$/g, "") || "unclassified";
                    setStorage((st) => ({ ...st, storage_prefix: `${slug}/` }));
                  }}
                />
              </Group>
              <Text size="xs" c="dimmed" mt={-6}>
                {CLUSTER_LABEL[identity.cluster] ??
                  (identity.cluster
                    ? "A classification of your own — it groups archives and filters search."
                    : "Unclassified until you set one.")}
              </Text>
              <TextInput
                label="Name"
                placeholder="Quality Records Archive"
                value={identity.name}
                onChange={(e) => setIdentity((v) => ({ ...v, name: e.currentTarget.value }))}
              />
              <Group grow>
                <TextInput
                  label="Source system"
                  description="The system these records came from"
                  placeholder="Legacy DMS"
                  value={identity.source_system}
                  onChange={(e) =>
                    setIdentity((v) => ({ ...v, source_system: e.currentTarget.value }))
                  }
                />
                <Select
                  label="Contents"
                  description="Documents, or tables from a retired application"
                  value={identity.kind}
                  data={[
                    { value: "documents", label: "Documents and files" },
                    { value: "datasets", label: "Tables from a retired system" },
                  ]}
                  onChange={(value) => setIdentity((v) => ({ ...v, kind: value ?? "documents" }))}
                />
              </Group>
              <Textarea
                label="Description"
                autosize
                minRows={2}
                value={identity.description}
                onChange={(e) => setIdentity((v) => ({ ...v, description: e.currentTarget.value }))}
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Metadata model" description="How it is described">
            <Stack gap="sm" mt="xl">
              <Text size="sm" c="dimmed">
                One definition, three consumers: these fields drive how an intake maps its metadata,
                what end users can filter on, and what the record detail shows.
              </Text>
              <Alert icon={<IconInfoCircle size={16} />} variant="light" color="navy">
                Classification is not decoration. A field marked <strong>pii</strong> or{" "}
                <strong>phi</strong> is never offered as a search facet — a distinct-value list over
                patient identifiers is a disclosure, not a filter.
              </Alert>
              <Table verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Key</Table.Th>
                    <Table.Th>Label</Table.Th>
                    <Table.Th w={100}>Type</Table.Th>
                    <Table.Th w={140}>Classification</Table.Th>
                    <Table.Th w={80}>Facet</Table.Th>
                    <Table.Th w={44} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {fields.map((field, index) => (
                    <Table.Tr key={index}>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={field.key}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f, i) =>
                                i === index ? { ...f, key: e.currentTarget.value } : f,
                              ),
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={field.label}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f, i) =>
                                i === index ? { ...f, label: e.currentTarget.value } : f,
                              ),
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          value={field.type}
                          data={["text", "date", "number"]}
                          onChange={(value) =>
                            setFields((prev) =>
                              prev.map((f, i) =>
                                i === index ? { ...f, type: value ?? "text" } : f,
                              ),
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          value={field.classification}
                          data={CLASSIFICATIONS}
                          onChange={(value) =>
                            setFields((prev) =>
                              prev.map((f, i) =>
                                i === index ? { ...f, classification: value ?? "confidential" } : f,
                              ),
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Switch
                          size="xs"
                          checked={field.facetable}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f, i) =>
                                i === index ? { ...f, facetable: e.currentTarget.checked } : f,
                              ),
                            )
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => setFields((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Button
                size="xs"
                variant="light"
                w="fit-content"
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  setFields((prev) => [
                    ...prev,
                    {
                      key: `field_${prev.length + 1}`,
                      label: `Field ${prev.length + 1}`,
                      type: "text",
                      facetable: true,
                      required: false,
                      classification: "confidential",
                    },
                  ])
                }
              >
                Add field
              </Button>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Retention" description="How long it is kept">
            <Stack gap="sm" mt="xl" maw={460}>
              <Select
                label="Retention class"
                description="Retain-until is derived from each record's own effective date, not its ingest date"
                value={retention.retention_class}
                data={["5y", "10y", "15y", "30y", "permanent"]}
                onChange={(value) =>
                  setRetention((v) => ({ ...v, retention_class: value ?? "10y" }))
                }
              />
              <Select
                label="Object Lock mode"
                description="Governance can be lifted by a privileged user; Compliance cannot be lifted by anyone"
                value={retention.object_lock_mode}
                data={["GOVERNANCE", "COMPLIANCE"]}
                onChange={(value) =>
                  setRetention((v) => ({ ...v, object_lock_mode: value ?? "GOVERNANCE" }))
                }
              />
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Storage" description="Where it lives">
            <Stack gap="sm" mt="xl" maw={520}>
              <TextInput
                label="Storage prefix"
                value={storage.storage_prefix}
                onChange={(e) =>
                  setStorage((v) => ({ ...v, storage_prefix: e.currentTarget.value }))
                }
              />
              <Select
                label="Storage class"
                description="Glacier Instant Retrieval keeps cold-storage economics with no retrieval delay"
                value={storage.storage_class}
                data={["STANDARD", "GLACIER_IR"]}
                onChange={(value) =>
                  setStorage((v) => ({ ...v, storage_class: value ?? "STANDARD" }))
                }
              />
              <Switch
                label="Index document text for full-text search"
                description="Extracted text is stored so it can be searched. Leave off for any corpus carrying personal data — it is not encrypted at rest in this build."
                checked={storage.content_indexing_enabled}
                onChange={(e) =>
                  setStorage((v) => ({ ...v, content_indexing_enabled: e.currentTarget.checked }))
                }
              />

              <Card withBorder padding="md" radius="md" mt="sm">
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="xs">
                  Summary
                </Text>
                <Stack gap={3}>
                  <Text size="sm">
                    <strong>{identity.name || "Unnamed"}</strong> — {clusterTitle(identity.cluster)}
                    , from {identity.source_system || "—"}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {fields.length} metadata fields · retention {retention.retention_class} · Object
                    Lock {retention.object_lock_mode}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {storage.storage_prefix} · {storage.storage_class}
                  </Text>
                </Stack>
              </Card>
            </Stack>
          </Stepper.Step>

          <Stepper.Step label="Access & rules" description="Who can see it">
            <Stack gap="sm" mt="xl">
              {created ? (
                <>
                  <Alert variant="light" color="teal">
                    <strong>{created.name}</strong> created. Grant access below, or finish and do it
                    later from the container&apos;s Access tab.
                  </Alert>
                  <ArchiveAccessTab containerId={created.container_id} canManage />
                </>
              ) : (
                <Alert variant="light" color="navy">
                  Create the archive first — access is granted against a container that exists.
                </Alert>
              )}
            </Stack>
          </Stepper.Step>
        </Stepper>

        <Group justify="space-between" mt="xl">
          <Button
            variant="subtle"
            onClick={() => (step === 0 ? navigate("/archive/containers") : setStep(step - 1))}
            disabled={!!created}
          >
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {step < 3 && (
            <Button disabled={!canContinue} onClick={() => setStep(step + 1)}>
              Continue
            </Button>
          )}
          {step === 3 && (
            <Button loading={busy} disabled={!canContinue} onClick={create}>
              Create archive
            </Button>
          )}
          {step === 4 && created && (
            <Button onClick={() => navigate(`/archive/containers/${created.container_id}`)}>
              Finish
            </Button>
          )}
        </Group>
      </Paper>
    </Stack>
  );
}
